import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AccountBalanceWalletOutlined,
  AssessmentOutlined,
  ArrowForward,
  Inventory2Outlined,
  PeopleAltOutlined,
  PointOfSaleOutlined,
  ReceiptLongOutlined,
  Refresh,
  ScheduleOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiClient } from '../../services/api';
import { DashboardSummary, Role } from '../../types';
import { errorMessage } from '../../utils/errorMessage';
import { formatCurrency } from '../../utils/currency';
import PageHeader from '../common/PageHeader';
import MetricCard from '../common/MetricCard';

type PeriodKey = 'month' | 'last30Days' | 'year';

interface DashboardRawData {
  summary: DashboardSummary;
}

interface DateRange {
  start: Date;
  end: Date;
}

interface ChartPoint {
  label: string;
  sales: number;
  collection: number;
  expenses: number;
}

type ChartSeriesKey = 'sales' | 'collection' | 'expenses';

type ChartSeriesVisibility = Record<ChartSeriesKey, boolean>;

interface DashboardAlert {
  icon: React.ReactNode;
  title: string;
  detail: string;
  color: string;
  path?: string;
}

const chartColor = (mode: 'light' | 'dark', series: ChartSeriesKey) => {
  const colors = {
    light: { sales: '#286B50', collection: '#C1782A', expenses: '#B94B5C' },
    dark: { sales: '#70C39A', collection: '#F0B35A', expenses: '#F28A99' },
  };
  return colors[mode][series];
};

const startOfDay = (date: Date) => {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
};

const endOfDay = (date: Date) => {
  const value = new Date(date);
  value.setHours(23, 59, 59, 999);
  return value;
};

const getDateRange = (period: PeriodKey, now = new Date()): DateRange => {
  const end = endOfDay(now);
  const start = new Date(now);

  if (period === 'month') {
    start.setDate(1);
  } else if (period === 'last30Days') {
    start.setDate(start.getDate() - 29);
  } else {
    start.setMonth(0, 1);
  }

  return { start: startOfDay(start), end };
};

const toDate = (value?: string) => {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    const localDate = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    return Number.isNaN(localDate.getTime()) ? null : localDate;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const sum = (values: number[]) => values.reduce((total, value) => total + (Number.isFinite(value) ? value : 0), 0);

const dateParam = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DashboardChart: React.FC<{
  points: ChartPoint[];
  salesLabel: string;
  collectionLabel: string;
  expensesLabel: string;
  visibility: ChartSeriesVisibility;
  onToggle: (series: ChartSeriesKey) => void;
  showExpenses: boolean;
}> = ({ points, salesLabel, collectionLabel, expensesLabel, visibility, onToggle, showExpenses }) => {
  const theme = useTheme();
  const salesColor = chartColor(theme.palette.mode, 'sales');
  const collectionColor = chartColor(theme.palette.mode, 'collection');
  const expensesColor = chartColor(theme.palette.mode, 'expenses');
  const width = 760;
  const height = 260;
  const top = 18;
  const bottom = 42;
  const chartHeight = height - top - bottom;
  const visibleSeries = (['sales', 'collection', ...(showExpenses ? ['expenses'] : [])] as ChartSeriesKey[])
    .filter(series => visibility[series]);
  const max = Math.max(1, ...points.flatMap(point => visibleSeries.map(series => point[series])));
  const getX = (index: number) => points.length <= 1 ? width / 2 : (index / (points.length - 1)) * width;
  const getY = (value: number) => top + chartHeight - (value / max) * chartHeight;
  const path = (field: ChartSeriesKey) => points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${getX(index).toFixed(1)} ${getY(point[field]).toFixed(1)}`)
    .join(' ');
  const series = [
    { key: 'sales' as const, label: salesLabel, color: salesColor },
    { key: 'collection' as const, label: collectionLabel, color: collectionColor },
    { key: 'expenses' as const, label: expensesLabel, color: expensesColor },
  ].filter(item => item.key !== 'expenses' || showExpenses);

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mb: 1.5 }}>
        {series.map(item => (
          <Button
            key={item.key}
            size="small"
            variant={visibility[item.key] ? 'outlined' : 'text'}
            aria-pressed={visibility[item.key]}
            onClick={() => onToggle(item.key)}
            sx={{ minWidth: 0, px: 1, py: 0.35, borderColor: alpha(item.color, 0.35), color: visibility[item.key] ? 'text.primary' : 'text.disabled', opacity: visibility[item.key] ? 1 : 0.65, textTransform: 'none' }}
          >
            <Box sx={{ width: 9, height: 9, mr: 0.75, borderRadius: '50%', bgcolor: item.color }} />
            <Typography variant="caption" component="span">{item.label}</Typography>
          </Button>
        ))}
      </Box>
      <svg
        role="img"
        aria-label={`${salesLabel} / ${collectionLabel}${showExpenses ? ` / ${expensesLabel}` : ''}`}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ display: 'block', width: '100%', height: 'clamp(190px, 27vw, 260px)' }}
      >
        {[0.25, 0.5, 0.75].map(ratio => (
          <line key={ratio} x1="0" x2={width} y1={getY(max * ratio)} y2={getY(max * ratio)} stroke={theme.palette.divider} strokeDasharray="4 8" />
        ))}
        {visibility.sales && <path d={path('sales')} fill="none" stroke={salesColor} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />}
        {visibility.collection && <path d={path('collection')} fill="none" stroke={collectionColor} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />}
        {showExpenses && visibility.expenses && <path d={path('expenses')} fill="none" stroke={expensesColor} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />}
        {points.map((point, index) => (
          <g key={`${point.label}-${index}`}>
            {visibility.sales && <circle cx={getX(index)} cy={getY(point.sales)} r="4" fill={theme.palette.background.paper} stroke={salesColor} strokeWidth="3" />}
            {visibility.collection && <circle cx={getX(index)} cy={getY(point.collection)} r="4" fill={theme.palette.background.paper} stroke={collectionColor} strokeWidth="3" />}
            {showExpenses && visibility.expenses && <circle cx={getX(index)} cy={getY(point.expenses)} r="4" fill={theme.palette.background.paper} stroke={expensesColor} strokeWidth="3" />}
            <text x={getX(index)} y={height - 10} textAnchor="middle" fontSize="12" fill={theme.palette.text.secondary}>{point.label}</text>
          </g>
        ))}
      </svg>
    </Box>
  );
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const theme = useTheme();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<PeriodKey>('month');
  const canSeeExpenses = user?.role === Role.SUPER_ADMIN || user?.role === Role.COMPANY_ADMIN;
  const scopeKey = `${user?.id ?? 'anonymous'}:${user?.companyId ?? 'none'}:${user?.role ?? 'none'}`;
  const range = getDateRange(period);
  const dashboardQuery = useQuery<DashboardSummary>({
    queryKey: ['dashboard', 'reports', scopeKey, period, dateParam(range.start), dateParam(range.end)],
    queryFn: ({ signal }) => apiClient.getDashboardSummary(dateParam(range.start), dateParam(range.end), signal),
    staleTime: 30_000,
    retry: false,
    enabled: Boolean(user),
  });
  const rawData = useMemo<DashboardRawData | null>(
    () => (dashboardQuery.data ? { summary: dashboardQuery.data } : null),
    [dashboardQuery.data],
  );
  const loading = dashboardQuery.isLoading;
  const error = dashboardQuery.error ? errorMessage(dashboardQuery.error, t('dashboard.loadError')) : null;
  const fetchDashboard = () => { void dashboardQuery.refetch(); };
  const rangeStartTime = range.start.getTime();
  const rangeEndTime = range.end.getTime();
  const [chartVisibility, setChartVisibility] = useState<ChartSeriesVisibility>({ sales: true, collection: true, expenses: true });

  useEffect(() => {
    setChartVisibility(current => ({ ...current, expenses: canSeeExpenses }));
  }, [canSeeExpenses]);

  const toggleChartSeries = (series: ChartSeriesKey) => {
    setChartVisibility(current => {
      const visibleCoreSeries = (['sales', 'collection'] as ChartSeriesKey[]).filter(key => current[key]);
      if (series !== 'expenses' && current[series] && visibleCoreSeries.length === 1) return current;
      return { ...current, [series]: !current[series] };
    });
  };

  const derived = useMemo(() => {
    const currentData = rawData;
    const summary = currentData?.summary;
    const startDate = new Date(rangeStartTime);
    const endDate = new Date(rangeEndTime);
    const bucketCount = period === 'year'
      ? endDate.getMonth() - startDate.getMonth() + 1
      : 7;
    const rangeLength = Math.max(1, rangeEndTime - rangeStartTime);
    const points = Array.from({ length: bucketCount }, (_, index) => {
      const bucketStart = period === 'year'
        ? new Date(startDate.getFullYear(), startDate.getMonth() + index, 1)
        : new Date(rangeStartTime + (rangeLength / bucketCount) * index);
      const bucketEnd = period === 'year'
        ? endOfDay(new Date(startDate.getFullYear(), startDate.getMonth() + index + 1, 0))
        : new Date(rangeStartTime + (rangeLength / bucketCount) * (index + 1));
      const isFinalBucket = index === bucketCount - 1;
      const isInBucket = (value: string | undefined) => {
        const date = toDate(value);
        return Boolean(date && date >= bucketStart && (isFinalBucket ? date <= bucketEnd : date < bucketEnd));
      };
      const bucketRows = (summary?.chart ?? []).filter(point => isInBucket(point.date));
      const label = period === 'year'
        ? bucketStart.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { month: 'short' })
        : bucketStart.toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { day: 'numeric' });
      return {
        label,
        sales: sum(bucketRows.map(point => point.sales)),
        collection: sum(bucketRows.map(point => point.collection)),
        expenses: sum(bucketRows.map(point => point.expenses)),
      };
    });

    return {
      totalSales: summary?.totalSales ?? 0,
      totalCollection: summary?.totalCollection ?? 0,
      totalReceivables: summary?.totalReceivables ?? 0,
      stockValue: summary?.stockValue ?? 0,
      zeroStockCount: summary?.zeroStockCount ?? 0,
      openSales: summary?.openSales ?? 0,
      customersWithBalance: summary?.customersWithBalance ?? 0,
      periodSalesCount: summary?.periodSalesCount ?? 0,
      unpaidExpenseCount: summary?.unpaidExpenseCount ?? 0,
      unpaidExpensesAmount: summary?.unpaidExpensesAmount ?? 0,
      batchCount: (summary?.expiredBatchCount ?? 0) + (summary?.expiringBatchCount ?? 0),
      topDebtors: (summary?.topDebtors ?? []).map(debtor => ({ name: debtor.customerName, amount: debtor.amount })),
      topSellingItems: (summary?.topSellingItems ?? []).map(item => ({
        name: item.itemName,
        quantity: item.quantity,
        amount: item.amount,
      })),
      paymentPlanAttention: summary?.paymentPlanAttention,
      chartPoints: points,
      showExpenses: canSeeExpenses,
    };
  }, [canSeeExpenses, language, period, rawData, rangeEndTime, rangeStartTime]);

  const alerts = useMemo<DashboardAlert[]>(() => {
    const items: DashboardAlert[] = [];
    if (derived.zeroStockCount > 0) {
      items.push({
        icon: <Inventory2Outlined fontSize="small" />,
        title: t('dashboard.outOfStockProducts'),
        detail: t('dashboard.outOfStockProductsDetail').replace('{0}', String(derived.zeroStockCount)),
        color: theme.palette.error.main,
        path: '/items',
      });
    }
    if (derived.unpaidExpenseCount > 0) {
      items.push({
        icon: <ReceiptLongOutlined fontSize="small" />,
        title: t('dashboard.unpaidExpenses'),
        detail: t('dashboard.unpaidExpensesDetail').replace('{0}', formatCurrency(derived.unpaidExpensesAmount)),
        color: theme.palette.warning.main,
        path: '/expenses',
      });
    }
    if (derived.batchCount > 0) {
      items.push({
        icon: <WarningAmberOutlined fontSize="small" />,
        title: t('dashboard.expiringBatches'),
        detail: t('dashboard.expiringBatchesDetail').replace('{0}', String(derived.batchCount)),
        color: theme.palette.info.main,
        path: '/items',
      });
    }
    const paymentPlanAttention = derived.paymentPlanAttention;
    if (paymentPlanAttention && paymentPlanAttention.overdueInstallmentCount > 0) {
      items.push({
        icon: <ScheduleOutlined fontSize="small" />,
        title: t('dashboard.overdueInstallments'),
        detail: t('dashboard.overdueInstallmentsDetail')
          .replace('{0}', String(paymentPlanAttention.overdueCustomerCount))
          .replace('{1}', String(paymentPlanAttention.overdueInstallmentCount))
          .replace('{2}', formatCurrency(paymentPlanAttention.overdueAmount)),
        color: theme.palette.error.main,
        path: '/debts',
      });
    }
    if (paymentPlanAttention && paymentPlanAttention.dueSoonInstallmentCount > 0) {
      items.push({
        icon: <ScheduleOutlined fontSize="small" />,
        title: t('dashboard.dueSoonInstallments'),
        detail: t('dashboard.dueSoonInstallmentsDetail')
          .replace('{0}', String(paymentPlanAttention.dueSoonCustomerCount))
          .replace('{1}', String(paymentPlanAttention.dueSoonInstallmentCount))
          .replace('{2}', formatCurrency(paymentPlanAttention.dueSoonAmount)),
        color: theme.palette.warning.main,
        path: '/debts?installmentFilter=soon',
      });
    }
    return items;
  }, [derived, t, theme.palette.error.main, theme.palette.info.main, theme.palette.warning.main]);

  const isEmpty = !loading && Boolean(rawData) && rawData?.summary.periodSalesCount === 0 && rawData.summary.topSellingItems.length === 0;
  const dataReady = Boolean(rawData);

  return (
    <Box sx={{ flexGrow: 1, pb: { xs: 2, md: 4 } }}>
      <PageHeader
        title={t('dashboard.reportsTitle')}
        description={t('dashboard.reportsHint')}
        icon={<AssessmentOutlined />}
        accent="reports"
        mobileMode="hidden"
        actions={(
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: { xs: 148, sm: 168 } }}>
              <InputLabel id="dashboard-period-label">{t('dashboard.period')}</InputLabel>
              <Select
                labelId="dashboard-period-label"
                value={period}
                label={t('dashboard.period')}
                disabled={loading}
                onChange={event => setPeriod(event.target.value as PeriodKey)}
              >
                <MenuItem value="month">{t('dashboard.thisMonth')}</MenuItem>
                <MenuItem value="last30Days">{t('dashboard.last30Days')}</MenuItem>
                <MenuItem value="year">{t('dashboard.thisYear')}</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title={t('dashboard.refresh')}>
              <IconButton aria-label={t('dashboard.refresh')} onClick={() => void fetchDashboard()} disabled={loading}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}
      {isEmpty && dataReady && <Alert severity="info" sx={{ mb: 2.5 }}>{t('dashboard.emptyState')}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, mb: 1.5 }}>
        <MetricCard
          title={t('dashboard.totalSales')}
          value={formatCurrency(derived.totalSales)}
          hint={t('dashboard.salesCount').replace('{0}', String(derived.periodSalesCount))}
          icon={<PointOfSaleOutlined />}
          color={chartColor(theme.palette.mode, 'sales')}
          loading={loading || !dataReady}
        />
        <MetricCard
          title={t('dashboard.totalCollection')}
          value={formatCurrency(derived.totalCollection)}
          hint={t('dashboard.periodCollectionHint')}
          icon={<AccountBalanceWalletOutlined />}
          color={chartColor(theme.palette.mode, 'collection')}
          loading={loading || !dataReady}
        />
        <MetricCard
          title={t('dashboard.totalReceivables')}
          value={formatCurrency(derived.totalReceivables)}
          hint={t('dashboard.receivablesHint').replace('{0}', String(derived.customersWithBalance)).replace('{1}', String(derived.openSales))}
          icon={<PeopleAltOutlined />}
          color={theme.palette.warning.main}
          loading={loading || !dataReady}
        />
      </Box>

      <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, mb: 1.5, borderRadius: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ display: 'grid', placeItems: 'center', width: 38, height: 38, borderRadius: 1.5, color: theme.palette.success.main, bgcolor: alpha(theme.palette.success.main, 0.12) }}>
              <Inventory2Outlined />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>{t('dashboard.stockSalesValue')}</Typography>
              <Typography sx={{ mt: 0.25, fontSize: 22, fontWeight: 800 }}>{loading || !dataReady ? <Skeleton width={140} /> : formatCurrency(derived.stockValue)}</Typography>
            </Box>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 360 }}>{t('dashboard.stockSalesValueHint')}</Typography>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.55fr) minmax(270px, .75fr)' }, gap: 1.5, mb: 1.5 }}>
        <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, borderRadius: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 0.5 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('dashboard.salesAndCollection')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('dashboard.salesAndCollectionHint')}</Typography>
            </Box>
          </Stack>
          {loading || !dataReady ? <Skeleton variant="rounded" height={250} sx={{ mt: 2 }} /> : (
            <DashboardChart
              points={derived.chartPoints}
              salesLabel={t('dashboard.sales')}
              collectionLabel={t('dashboard.collection')}
              expensesLabel={t('dashboard.expenses')}
              visibility={chartVisibility}
              onToggle={toggleChartSeries}
              showExpenses={derived.showExpenses}
            />
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, borderRadius: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.75 }}>
            <Box sx={{ display: 'grid', placeItems: 'center', width: 34, height: 34, borderRadius: 1.25, color: theme.palette.warning.main, bgcolor: alpha(theme.palette.warning.main, 0.12) }}>
              <WarningAmberOutlined fontSize="small" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('dashboard.attention')}</Typography>
          </Stack>
          {loading || !dataReady ? (
            <Stack spacing={1.25}>{[1, 2].map(item => <Skeleton key={item} variant="rounded" height={58} />)}</Stack>
          ) : alerts.length === 0 ? (
            <Typography color="text.secondary" variant="body2">{t('dashboard.noAlerts')}</Typography>
          ) : (
            <Stack spacing={1}>
              {alerts.map(alert => (
                <Button
                  key={alert.title}
                  onClick={() => alert.path && navigate(alert.path)}
                  sx={{ p: 1, justifyContent: 'flex-start', textAlign: 'left', color: 'text.primary', borderRadius: 1.5, bgcolor: alpha(alert.color, 0.07), '&:hover': { bgcolor: alpha(alert.color, 0.13) } }}
                  endIcon={<ArrowForward sx={{ color: alert.color }} />}
                >
                  <Box sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, mr: 1, flexShrink: 0, borderRadius: 1, color: alert.color, bgcolor: alpha(alert.color, 0.12) }}>{alert.icon}</Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{alert.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{alert.detail}</Typography>
                  </Box>
                </Button>
              ))}
            </Stack>
          )}
        </Paper>
      </Box>

      {(loading || derived.topDebtors.length > 0 || derived.topSellingItems.length > 0) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5, mb: 1.5 }}>
          <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, borderRadius: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'grid', placeItems: 'center', width: 34, height: 34, borderRadius: 1.25, color: chartColor(theme.palette.mode, 'collection'), bgcolor: alpha(chartColor(theme.palette.mode, 'collection'), 0.12) }}>
                <PeopleAltOutlined fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('dashboard.topDebtors')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('dashboard.topDebtorsHint')}</Typography>
              </Box>
            </Stack>
            {loading || !dataReady ? <Stack spacing={1}><Skeleton variant="rounded" height={48} /><Skeleton variant="rounded" height={48} /></Stack> : derived.topDebtors.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('dashboard.noTopDebtors')}</Typography>
            ) : (
              <Stack spacing={0.75}>
                {derived.topDebtors.map((debtor, index) => (
                  <Button key={debtor.name} onClick={() => navigate('/debts')} sx={{ justifyContent: 'flex-start', textAlign: 'left', px: 1, py: 0.75, color: 'text.primary', borderRadius: 1.25, '&:hover': { bgcolor: 'action.hover' } }}>
                    <Typography variant="caption" sx={{ width: 24, color: 'text.secondary', fontWeight: 800 }}>{index + 1}</Typography>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>{debtor.name}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 800 }}>{formatCurrency(debtor.amount)}</Typography>
                  </Button>
                ))}
              </Stack>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2.25 }, borderRadius: 2.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'grid', placeItems: 'center', width: 34, height: 34, borderRadius: 1.25, color: chartColor(theme.palette.mode, 'sales'), bgcolor: alpha(chartColor(theme.palette.mode, 'sales'), 0.12) }}>
                <PointOfSaleOutlined fontSize="small" />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('dashboard.topSellingItems')}</Typography>
                <Typography variant="caption" color="text.secondary">{t('dashboard.topSellingItemsHint')}</Typography>
              </Box>
            </Stack>
            {loading || !dataReady ? <Stack spacing={1}><Skeleton variant="rounded" height={48} /><Skeleton variant="rounded" height={48} /></Stack> : derived.topSellingItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{t('dashboard.noTopSellingItems')}</Typography>
            ) : (
              <Stack spacing={0.75}>
                {derived.topSellingItems.map((item, index) => (
                  <Button key={item.name} onClick={() => navigate('/items')} sx={{ justifyContent: 'flex-start', textAlign: 'left', px: 1, py: 0.75, color: 'text.primary', borderRadius: 1.25, '&:hover': { bgcolor: 'action.hover' } }}>
                    <Typography variant="caption" sx={{ width: 24, color: 'text.secondary', fontWeight: 800 }}>{index + 1}</Typography>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>{item.name}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 800 }}>{item.quantity} {t('dashboard.quantity')}</Typography>
                  </Button>
                ))}
              </Stack>
            )}
          </Paper>
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 1.75, md: 2 }, borderRadius: 2.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PeopleAltOutlined color="primary" />
            <Box>
              <Typography sx={{ fontWeight: 800 }}>{t('dashboard.detailLinksTitle')}</Typography>
              <Typography variant="caption" color="text.secondary">{t('dashboard.batchExpiryHint')}</Typography>
            </Box>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="outlined" endIcon={<ArrowForward />} onClick={() => navigate('/debts')}>
              {t('dashboard.openReceivables')}
            </Button>
            <Button variant="outlined" endIcon={<ArrowForward />} onClick={() => navigate('/items')}>
              {t('dashboard.openStock')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
};

export default Dashboard;
